<template>
  <div class="page">
    <div class="card">
      <div class="card-header">
        <div>
          <h1 class="title">Chamados</h1>
          <p class="subtitle">Abertura e acompanhamento de chamados operacionais.</p>
        </div>
      </div>

      <form class="form-grid" @submit.prevent="createTicket">
        <div class="grid" style="grid-template-columns: repeat(auto-fit,minmax(200px,1fr)); gap: 10px;">
          <input v-model="form.title" type="text" placeholder="Titulo" />
          <select v-model="form.category">
            <option value="manutencao-ferramentas">Manutencao de ferramentas</option>
            <option value="problema-projeto">Problema no projeto/obra</option>
            <option value="solicitacao-recursos">Solicitacao de recursos</option>
            <option value="suporte-tecnico">Suporte tecnico</option>
            <option value="seguranca-trabalho">Seguranca do trabalho</option>
            <option value="outros">Outros</option>
          </select>
          <select v-model="form.priority">
            <option value="critica">Critica</option>
            <option value="alta">Alta</option>
            <option value="media">Media</option>
            <option value="baixa">Baixa</option>
          </select>
          <input v-model="form.projectId" type="text" placeholder="Projeto (ID opcional)" />
          <input v-model="form.taskId" type="text" placeholder="Tarefa (ID opcional)" />
          <input v-model="form.toolItemId" type="text" placeholder="Ferramenta (ID opcional)" />
          <input v-model="form.location" type="text" placeholder="Localizacao" />
          <input v-model="form.desiredDueDate" type="date" placeholder="Prazo desejado" />
        </div>
        <textarea v-model="form.description" rows="3" placeholder="Descricao detalhada"></textarea>
        <button class="btn btn-primary" type="submit">Novo chamado</button>
      </form>

      <div class="section">
        <h2 class="section-title">Chamados</h2>
        <p v-if="loadError" class="error-text">{{ loadError }}</p>
        <SkeletonList v-if="showSkeleton" variant="row" :count="4" :columns="[30, 40, 20]" />
        <div v-else-if="items.length === 0" class="hint">Nenhum chamado.</div>
        <div v-else class="list">
          <div v-for="item in items" :key="item.id" class="item-card">
            <strong>{{ item.title }}</strong>
            <div class="item-meta">{{ item.category }} | prioridade: {{ item.priority }} | status: {{ item.status }}</div>
            <div class="item-meta">Projeto: {{ item.projectId || '-' }} | Tarefa: {{ item.taskId || '-' }}</div>
            <div class="actions">
              <button class="btn btn-ghost" type="button" @click="claim(item.id)">Assumir</button>
              <button class="btn btn-ghost" type="button" @click="setStatus(item.id, 'em-andamento')">Em andamento</button>
              <button class="btn btn-ghost" type="button" @click="setStatus(item.id, 'resolvido')">Resolvido</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, ref } from 'vue'
import { ticketsStore } from '@/store/tickets'
import SkeletonList from '@/components/skeleton/SkeletonList.vue'

export default defineComponent({
  name: 'TicketsView',
  components: { SkeletonList },
  setup() {
    const loadError = ref('')
    const form = ref({
      title: '',
      category: 'problema-projeto',
      priority: 'media' as 'critica' | 'alta' | 'media' | 'baixa',
      description: '',
      projectId: '',
      taskId: '',
      toolItemId: '',
      location: '',
      desiredDueDate: ''
    })
    const items = computed(() => ticketsStore.all())
    const showSkeleton = computed(() => ticketsStore.state.loading && items.value.length === 0)

    const createTicket = async () => {
      if (!form.value.title.trim() || !form.value.category.trim()) return
      await ticketsStore.create({
        ...form.value,
        title: form.value.title.trim(),
        category: form.value.category.trim(),
        description: form.value.description.trim(),
        projectId: form.value.projectId.trim(),
        taskId: form.value.taskId.trim(),
        toolItemId: form.value.toolItemId.trim(),
        location: form.value.location.trim()
      })
      form.value = {
        title: '',
        category: 'problema-projeto',
        priority: 'media',
        description: '',
        projectId: '',
        taskId: '',
        toolItemId: '',
        location: '',
        desiredDueDate: ''
      }
    }

    const claim = async (id: string) => {
      await ticketsStore.claim(id)
    }
    const setStatus = async (id: string, status: string) => {
      await ticketsStore.setStatus(id, status)
    }

    onMounted(() => {
      ticketsStore.refresh().catch(() => {
        loadError.value = 'Falha ao carregar chamados. Tente recarregar a página.'
      })
    })

    return {
      form,
      items,
      loadError,
      showSkeleton,
      createTicket,
      claim,
      setStatus
    }
  }
})
</script>

<style scoped lang="scss">
.error-text {
  color: #dc2626;
  font-size: 13px;
  margin: 0 0 10px;
}

.actions {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 4px;
}

@media (max-width: 720px) {
  .actions {
    flex-direction: column;
  }

  .actions .btn {
    width: 100%;
    justify-content: center;
  }
}
</style>
